import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  DAYS_ON_MARKET_FILTER_1_30,
  DAYS_ON_MARKET_FILTER_30_60,
  DAYS_ON_MARKET_FILTER_60,
  LISTING_STATUS_ACTIVE,
  LISTING_STATUS_NOT_AVAILABLE,
  LISTING_PROPERTY_OWNER_INDIVIDUAL,
  LISTING_PROPERTY_OWNER_INSTITUTION,
} from 'common/listingConstants';

import { SR_API_ACTION_PARTNER_UPDATE_SUNSETUP_LISTING } from 'api/actionTypes';

import { DATA_TABLE_SOURCE_SUNSETUP_COMPLETED_SETUPS } from 'common/dataTableConstants';

import postWithState from 'client/actions/postWithState';
import SrText from 'client/components/SrText';
import SrPartnerSection from 'client/components/SrPartnerSection';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrCompletedSetupsTable from 'client/components/SrCompletedSetupsTable/SrCompletedSetupsTable';
import SrBox from 'client/components/SrBox';
import SrUserSettingFilterListDropdown from 'client/components/SrUserSettingFilterListDropdown';
import SrUserSettingDropdownWithSearchBar from 'client/components/SrUserSettingDropdownWithSearchBar/SrUserSettingDropdownWithSearchBar';

function getDataTableSettingsFromProps(props) {
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_SUNSETUP_COMPLETED_SETUPS,
    rootViewId: DATA_TABLE_SOURCE_SUNSETUP_COMPLETED_SETUPS,
    initialViewSettings: {
      filter: {
        partnerId: props.partner.id,
      },
      sort: {
        createdDate: 'desc',
      },
      includeItemsCount: true,
      loadAllData: true,
      customParams: {
        partnerListings: {
          excludeUninstallJobs: true,
        },
        completedSetups: {},
      },
    },
    itemsPropName: 'completedSetups',
    count: 30,
    sortUserSettingsFieldId: 'adminPartners.sorting',
  };
  const marketIds = props[props.partner.slug + '_marketIds']; // eslint-disable-line max-len

  if (props.sorting) {
    dataTableSettings.initialViewSettings.sort = props.sorting;
  }

  if (props.isLiveFilterId && props.isLiveFilterId.length > 0) {
    dataTableSettings.initialViewSettings.filter.status = props.isLiveFilterId; // eslint-disable-line max-len
    dataTableSettings.rootViewId = `${dataTableSettings.rootViewId}_isLiveFilterId:${props.isLiveFilterId}`; // eslint-disable-line max-len
  }

  dataTableSettings.initialViewSettings.filter.cantTourFlag = props.isTourableFilterId; // eslint-disable-line max-len
  dataTableSettings.rootViewId = `${dataTableSettings.rootViewId}_isTourableFilterId:${props.isTourableFilterId}`; // eslint-disable-line max-len

  if (marketIds && marketIds.length > 0) {
    dataTableSettings.initialViewSettings.filter.marketId = marketIds; // eslint-disable-line max-len
    dataTableSettings.rootViewId = `${dataTableSettings.rootViewId}_marketIds:${props.isLiveFilterId}`; // eslint-disable-line max-len
  }

  if (props.propertyOwnerType) {
    dataTableSettings.initialViewSettings.filter.propertyOwnerType = props.propertyOwnerType; // eslint-disable-line max-len
    dataTableSettings.rootViewId = `${dataTableSettings.rootViewId}_propertyOwnerType:${props.propertyOwnerType}`; // eslint-disable-line max-len
  }

  if (props.daysOnMarket) {
    dataTableSettings.initialViewSettings.customParams.completedSetups.daysOnMarket =
      props.daysOnMarket;
    dataTableSettings.rootViewId = `${dataTableSettings.rootViewId}_daysOnMarket:${props.daysOnMarket}`;
  }

  return dataTableSettings;
}

const SrDataTableSetupCompletedPartnerListings = ({ partner, dispatch, currentUser }) => {
  const [searchTerm, setSearchTerm] = useState('');

  return (
    <SrDataTableConnected
      {...getDataTableSettingsFromProps({
        ...currentUser.settings.completedSetupsSettings,
        partner,
      })}
    >
      {({
        completedSetups,
        viewSettings,
        reloadItems,
        dataTableApiState,
        dataTableIsFullyLoaded,
      }) => {
        const noItems = dataTableIsFullyLoaded && completedSetups.length === 0;

        const handleIsTourableUpdate = ({ listingId }) => {
          const completedSetup = completedSetups.find((completedSetup) => {
            return completedSetup.listingId === listingId;
          });
          if (!completedSetup) {
            return;
          }
          dispatch(postWithState, {
            action: SR_API_ACTION_PARTNER_UPDATE_SUNSETUP_LISTING,
            payload: {
              listingId,
              updates: {
                cantTourFlag: !completedSetup.cantTourFlag,
              },
            },
          }).then(() => {
            reloadItems({
              count: 1,
              filter: {
                'listings.id': listingId,
              },
            });
          });
        };

        const handleIsLiveUpdate = ({ listingId }) => {
          const completedSetup = completedSetups.find((completedSetup) => {
            return completedSetup.listingId === listingId;
          });
          if (!completedSetup) {
            return;
          }
          dispatch(postWithState, {
            action: SR_API_ACTION_PARTNER_UPDATE_SUNSETUP_LISTING,
            payload: {
              listingId,
              updates: {
                status:
                  completedSetup.status === LISTING_STATUS_ACTIVE
                    ? LISTING_STATUS_NOT_AVAILABLE
                    : LISTING_STATUS_ACTIVE,
              },
            },
          }).then(() => {
            reloadItems({
              count: 1,
              filter: {
                'listings.id': listingId,
              },
            });
          });
        };

        const handleSearchSubmit = (event) => {
          event.preventDefault();
          viewSettings.update({
            ...viewSettings,
            fuzzy: !searchTerm
              ? null
              : {
                  queryString: searchTerm,
                  columns: ['addressStreetDisplay', 'propertyOwnerName', 'propertySecondOwnerName'],
                },
          });
        };

        const handleSearchChange = (event) => {
          event.preventDefault();
          setSearchTerm(event.target.value || '');
        };

        const tourableOptions = [
          {
            name: 'Tourable',
            id: false,
          },
          {
            name: 'Not Tourable',
            id: true,
          },
        ];
        const statusOptions = [
          {
            name: 'Live',
            id: LISTING_STATUS_ACTIVE,
          },
          {
            name: 'Not Live',
            id: LISTING_STATUS_NOT_AVAILABLE,
          },
        ];
        const ownerOptions = [
          {
            name: 'Institutional',
            id: LISTING_PROPERTY_OWNER_INSTITUTION,
          },
          {
            name: 'Retail',
            id: LISTING_PROPERTY_OWNER_INDIVIDUAL,
          },
        ];
        const daysOnMarketOptions = [
          {
            name: '1-30 Days',
            id: DAYS_ON_MARKET_FILTER_1_30,
          },
          {
            name: '30-60 Days',
            id: DAYS_ON_MARKET_FILTER_30_60,
          },
          {
            name: '60+ days',
            id: DAYS_ON_MARKET_FILTER_60,
          },
        ];

        return (
          <div>
            <SrPartnerSection
              title="Completed Setups"
              headerRow={
                <div>
                  <div className="flex" style={{ lineHeight: '40px' }}>
                    <div key="completedSetups-marketFilter" style={{ margin: '0 0 0 25px' }}>
                      <SrUserSettingDropdownWithSearchBar
                        useUppercase
                        title="Market"
                        notFoundTitle="No markets found"
                        settingField={`completedSetupsSettings.${partner.slug}_marketIds`}
                        dropdownOptions={partner.markets.map((option) => ({
                          label: option.displayNameFull,
                          value: option.marketId,
                        }))}
                      />
                    </div>
                    <div key="completedSetups-statusFilter" style={{ margin: '0 0 0 25px' }}>
                      <SrUserSettingFilterListDropdown
                        currentUser={currentUser}
                        settingField="completedSetupsSettings.isLiveFilterId"
                        placeholder="Status"
                        optionsList={statusOptions}
                      />
                    </div>
                    <div key="completedSetups-isTourableFilter" style={{ margin: '0 0 0 25px' }}>
                      <SrUserSettingFilterListDropdown
                        currentUser={currentUser}
                        settingField="completedSetupsSettings.isTourableFilterId"
                        placeholder="Tours"
                        optionsList={tourableOptions}
                      />
                    </div>
                    <div key="completedSetups-ownerTypeFilterId" style={{ margin: '0 0 0 25px' }}>
                      <SrUserSettingFilterListDropdown
                        singleSelection
                        currentUser={currentUser}
                        settingField="completedSetupsSettings.propertyOwnerType"
                        placeholder="Owner"
                        optionsList={ownerOptions}
                      />
                    </div>
                    <div key="completedSetups-daysOnMarketFilter" style={{ margin: '0 0 0 25px' }}>
                      <SrUserSettingFilterListDropdown
                        currentUser={currentUser}
                        settingField="completedSetupsSettings.daysOnMarket"
                        placeholder="DOM"
                        optionsList={daysOnMarketOptions}
                      />
                    </div>
                  </div>
                  <SrBox position="absolute" top="7px" right="0">
                    <form method="post" onSubmit={handleSearchSubmit} className="searchContainer">
                      <input
                        style={{
                          paddingLeft: '8px',
                          paddingBottom: '2px',
                          height: '33px',
                          width: '260px',
                          fontSize: '13px',
                          boxShadow: 'inset 0 0 0 1px rgba(123, 132, 136, 0.33)',
                          borderRadius: '8px',
                          boxSizing: 'border-box',
                          fontFamily: 'Greycliff',
                        }}
                        onChange={handleSearchChange}
                        value={searchTerm}
                        placeholder="Search address or owner"
                      />
                    </form>
                  </SrBox>
                </div>
              }
            >
              <SrBox paddingTop="11px">
                {noItems && (
                  <SrText font="greycliff" weight="600" color="grey" fontSize="16px">
                    There are no active listings right now.
                  </SrText>
                )}

                <SrCompletedSetupsTable
                  completedSetups={completedSetups}
                  handleIsTourableUpdate={handleIsTourableUpdate}
                  handleIsLiveUpdate={handleIsLiveUpdate}
                  partnerSlug={partner.slug}
                  reloadItems={reloadItems}
                />

                {dataTableApiState.inProgress && <div className="loading-ellipses" />}
              </SrBox>
            </SrPartnerSection>
          </div>
        );
      }}
    </SrDataTableConnected>
  );
};

SrDataTableSetupCompletedPartnerListings.propTypes = {
  partner: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

SrDataTableSetupCompletedPartnerListings.displayName = 'SrDataTableSetupCompletedPartnerListings';

export default SrDataTableSetupCompletedPartnerListings;
